import React, { useEffect, useState } from 'react';
import { fetchWithNonce } from './utils/fetchWithNonce';
import ProfileAvatar from '../../hub-shared/components/ProfileAvatar';
import type { PortalBootstrapData } from './types/window';
import {
  deconnexionIcon,
  downArrowIcon,
  liaisonsIcon,
  mesDossiersIcon,
  profilIcon,
  sidebarNotificationIcon,
  utilisateursIcon,
  vsmeIcon,
} from '@tpct/admin-ui';
import { __ } from '@/hub-shared/utils/i18n';

const normalizePath = (value: string) =>
  value.length > 1 && value.endsWith('/') ? value.slice(0, -1) : value;

type PortalSidebarProps = {
  bootstrap: PortalBootstrapData;
  pathname: string;
  onNavigate: (path: string) => void;
  unreadNotificationCount: number;
  onUnreadNotificationCountChange: (count: number) => void;
};

const StaticExpandIcon = () => (
  <span className="acchub-sidebar-expandButton acchub-sidebar-expandButton--static" aria-hidden="true">
    <img src={downArrowIcon} alt="" className="acchub-sidebar-expandIcon" aria-hidden="true" />
  </span>
);

const PortalSidebar = ({
  bootstrap,
  pathname,
  onNavigate,
  unreadNotificationCount,
  onUnreadNotificationCountChange,
}: PortalSidebarProps) => {
  const portalPath = normalizePath(bootstrap.portalPath);
  const user = bootstrap.user ?? { displayName: '', email: '', avatarUrl: '' };
  const brand = bootstrap.brand ?? { logoUrl: '', name: 'Accounting Hub' };
  const caps = bootstrap.capabilities ?? { manageVsme: false, manageOptions: false };

  const cabinetId = bootstrap.cabinetId;
  const hasCabinetScope = cabinetId !== null && cabinetId !== undefined && cabinetId > 0;
  const roles = bootstrap.currentUser?.roles ?? [];
  const showCabinetNav =
    hasCabinetScope && !roles.includes('utilisateur_entreprise');

  const isVsmeRoute =
    normalizePath(pathname).startsWith(normalizePath(`${portalPath}/vsme`)) ||
    normalizePath(pathname).startsWith(normalizePath(`${portalPath}/missions`));
  const [vsmeExpanded, setVsmeExpanded] = useState(isVsmeRoute);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const hasVsme = caps.manageVsme;

  useEffect(() => {
    setVsmeExpanded(isVsmeRoute);
  }, [isVsmeRoute]);

  useEffect(() => {
    if (!hasCabinetScope) {
      onUnreadNotificationCountChange(0);
      return;
    }

    let cancelled = false;
    const loadUnreadCount = async () => {
      try {
        const response = await fetchWithNonce(
          `${bootstrap.restUrls.accountingHub}notifications/unread-count`,
          {
            method: 'GET',
            credentials: 'same-origin',
            headers: { 'X-WP-Nonce': bootstrap.nonce },
          },
        );
        if (!response.ok || cancelled) {
          return;
        }
        const json = (await response.json()) as { data?: { unread_count?: number } };
        if (!cancelled && typeof json.data?.unread_count === 'number') {
          onUnreadNotificationCountChange(json.data.unread_count);
        }
      } catch {
        /* badge is optional */
      }
    };

    void loadUnreadCount();
    return () => {
      cancelled = true;
    };
  }, [
    bootstrap.nonce,
    bootstrap.restUrls.accountingHub,
    hasCabinetScope,
    onUnreadNotificationCountChange,
  ]);

  const isActive = (path: string) =>
    normalizePath(pathname) === normalizePath(`${portalPath}${path}`);

  const escapedPortal = portalPath.replace(/[/\\^$*+?.()|[\]{}]/g, '\\$&');
  const isCompaniesSectionActive = (() => {
    const current = normalizePath(pathname);
    const list = normalizePath(`${portalPath}/companies`);
    if (current === list) {
      return true;
    }
    return (
      new RegExp(`^${escapedPortal}/companies/company/\\d+$`).test(current) ||
      new RegExp(`^${escapedPortal}/company/\\d+$`).test(current)
    );
  })();

  const isVsmeSubActive = (path: string, prefixMatch = false) => {
    const base = normalizePath(`${portalPath}/vsme${path}`);
    const current = normalizePath(pathname);
    return prefixMatch ? current.startsWith(base) : current === base;
  };

  const handleNav = (path: string, isLogout: boolean) => {
    if (isLogout) {
      setShowLogoutModal(true);
      return;
    }
    onNavigate(path);
  };

  const confirmLogout = () => {
    window.location.href = bootstrap.logoutUrl;
  };

  return (
    <div className="acchub-sidebar-shell">
      <div className="acchub-sidebar-brand">
        <img src={brand.logoUrl} alt={brand.name} className="acchub-sidebar-brandIcon" />
        <span className="acchub-sidebar-brandName">{brand.name}</span>
      </div>

      <nav className="acchub-sidebar-nav" aria-label="Navigation portail">
        <div className="acchub-sidebar-menuGroups">
          {hasVsme && (
            <ul className="acchub-sidebar-menu acchub-sidebar-menu--group-vsme">
              <li
                className={[
                  'acchub-sidebar-item',
                  isVsmeRoute ? 'acchub-sidebar-item--active' : '',
                  vsmeExpanded ? 'is-open' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <div className="acchub-sidebar-itemHeader">
                  <a
                    href={`${portalPath}/vsme`}
                    className="acchub-sidebar-link"
                    onClick={(e) => {
                      e.preventDefault();
                      handleNav('/vsme', false);
                    }}
                    aria-expanded={vsmeExpanded}
                    aria-controls="acchub-sidebar-submenu-vsme"
                  >
                    <img src={vsmeIcon} alt="" className="acchub-sidebar-icon" aria-hidden />
                    <span className="acchub-sidebar-label">VS (vsme)</span>
                    <button
                      type="button"
                      className="acchub-sidebar-expandButton"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setVsmeExpanded((v) => !v);
                      }}
                      aria-label={vsmeExpanded ? __('Replier VS (vsme)') : __('Déplier VS (vsme)')}
                      aria-expanded={vsmeExpanded}
                    >
                      <img
                        src={downArrowIcon}
                        alt=""
                        aria-hidden
                        className={['acchub-sidebar-expandIcon', vsmeExpanded ? 'is-up' : ''].filter(Boolean).join(' ')}
                      />
                    </button>
                  </a>
                </div>
                {vsmeExpanded && (
                  <ul id="acchub-sidebar-submenu-vsme" className="acchub-sidebar-submenu">
                    <li className={isVsmeSubActive('') ? 'is-current acchub-sidebar-subitem--active' : ''}>
                      <a
                        href={`${portalPath}/vsme`}
                        className="acchub-sidebar-sublink"
                        onClick={(e) => {
                          e.preventDefault();
                          handleNav('/vsme', false);
                        }}
                      >
                        <span>{__('Tableau de bord')}</span>
                        {isVsmeSubActive('') ? (
                          <img src={downArrowIcon} alt="" aria-hidden className="acchub-sidebar-subitemIndicator" />
                        ) : null}
                      </a>
                    </li>
                    <li className={isVsmeSubActive('/missions', true) ? 'is-current acchub-sidebar-subitem--active' : ''}>
                      <a
                        href={`${portalPath}/vsme/missions`}
                        className="acchub-sidebar-sublink"
                        onClick={(e) => {
                          e.preventDefault();
                          handleNav('/vsme/missions', false);
                        }}
                      >
                        <span>{__('Missions VS')}</span>
                        {isVsmeSubActive('/missions', true) ? (
                          <img src={downArrowIcon} alt="" aria-hidden className="acchub-sidebar-subitemIndicator" />
                        ) : null}
                      </a>
                    </li>
                    {caps.manageOptions && (
                      <li className={isVsmeSubActive('/settings') ? 'is-current acchub-sidebar-subitem--active' : ''}>
                        <a
                          href={`${portalPath}/vsme/settings`}
                          className="acchub-sidebar-sublink"
                          onClick={(e) => {
                            e.preventDefault();
                            handleNav('/vsme/settings', false);
                          }}
                        >
                          <span>{__('Réglages')}</span>
                          {isVsmeSubActive('/settings') ? (
                            <img src={downArrowIcon} alt="" aria-hidden className="acchub-sidebar-subitemIndicator" />
                          ) : null}
                        </a>
                      </li>
                    )}
                  </ul>
                )}
              </li>
            </ul>
          )}

          <ul className="acchub-sidebar-menu acchub-sidebar-menu--group-core">
            {(caps.manageEnterprises || caps.viewEnterprises) && (
              <li
                className={['acchub-sidebar-item', isCompaniesSectionActive ? 'acchub-sidebar-item--active' : '']
                  .filter(Boolean)
                  .join(' ')}
              >
                <div className="acchub-sidebar-itemHeader">
                  <a
                    href={`${portalPath}/companies`}
                    className="acchub-sidebar-link"
                    onClick={(e) => {
                      e.preventDefault();
                      handleNav('/companies', false);
                    }}
                    aria-current={isCompaniesSectionActive ? 'page' : undefined}
                  >
                    <img src={mesDossiersIcon} alt="" className="acchub-sidebar-icon" aria-hidden />
                    <span className="acchub-sidebar-label">{__('Mes dossiers')}</span>
                    <StaticExpandIcon />
                  </a>
                </div>
              </li>
            )}
            {caps.viewUsers && (
            <li
              className={['acchub-sidebar-item', isActive('/users') ? 'acchub-sidebar-item--active' : '']
                .filter(Boolean)
                .join(' ')}
            >
              <div className="acchub-sidebar-itemHeader">
                <a
                  href={`${portalPath}/users`}
                  className="acchub-sidebar-link"
                  onClick={(e) => {
                    e.preventDefault();
                    handleNav('/users', false);
                  }}
                  aria-current={isActive('/users') ? 'page' : undefined}
                >
                  <img src={utilisateursIcon} alt="" className="acchub-sidebar-icon" aria-hidden />
                  <span className="acchub-sidebar-label">{__('Mes utilisateurs')}</span>
                  <StaticExpandIcon />
                </a>
              </div>
            </li>
            )}
            {(caps.viewConnections ?? caps.manageEnterprises ?? caps.viewEnterprises) && (
              <li
                className={['acchub-sidebar-item', isActive('/connections') ? 'acchub-sidebar-item--active' : '']
                  .filter(Boolean)
                  .join(' ')}
              >
                <div className="acchub-sidebar-itemHeader">
                  <a
                    href={`${portalPath}/connections`}
                    className="acchub-sidebar-link"
                    onClick={(e) => {
                      e.preventDefault();
                      handleNav('/connections', false);
                    }}
                    aria-current={isActive('/connections') ? 'page' : undefined}
                  >
                    <img src={liaisonsIcon} alt="" className="acchub-sidebar-icon" aria-hidden />
                    <span className="acchub-sidebar-label">{__('Connexions')}</span>
                    <StaticExpandIcon />
                  </a>
                </div>
              </li>
            )}
          </ul>

          <ul className="acchub-sidebar-menu acchub-sidebar-menu--group-account">
            {showCabinetNav && (
              <li
                className={['acchub-sidebar-item', isActive('/cabinet') ? 'acchub-sidebar-item--active' : '']
                  .filter(Boolean)
                  .join(' ')}
              >
                <div className="acchub-sidebar-itemHeader">
                  <a
                    href={`${portalPath}/cabinet`}
                    className="acchub-sidebar-link"
                    onClick={(e) => {
                      e.preventDefault();
                      handleNav('/cabinet', false);
                    }}
                    aria-current={isActive('/cabinet') ? 'page' : undefined}
                  >
                    <img src={mesDossiersIcon} alt="" className="acchub-sidebar-icon" aria-hidden />
                    <span className="acchub-sidebar-label">{__('Mon cabinet')}</span>
                    <StaticExpandIcon />
                  </a>
                </div>
              </li>
            )}
            {hasCabinetScope ? (
              <li
                className={[
                  'acchub-sidebar-item',
                  isActive('/notifications') ? 'acchub-sidebar-item--active' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <div className="acchub-sidebar-itemHeader">
                  <a
                    href={`${portalPath}/notifications`}
                    className="acchub-sidebar-link"
                    onClick={(e) => {
                      e.preventDefault();
                      handleNav('/notifications', false);
                    }}
                    aria-current={isActive('/notifications') ? 'page' : undefined}
                  >
                    <img src={sidebarNotificationIcon} alt="" className="acchub-sidebar-icon" aria-hidden />
                    <span className="acchub-sidebar-label">{__('Notifications')}</span>
                    {unreadNotificationCount > 0 ? (
                      <span className="acchub-sidebar-unread-count" aria-label={__('Notifications non lues')}>
                        <span className="acchub-sidebar-unread-count__value">
                          {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
                        </span>
                      </span>
                    ) : null}
                    <StaticExpandIcon />
                  </a>
                </div>
              </li>
            ) : null}
            <li
              className={['acchub-sidebar-item', isActive('/profile') ? 'acchub-sidebar-item--active' : '']
                .filter(Boolean)
                .join(' ')}
            >
              <div className="acchub-sidebar-itemHeader">
                <a
                  href={`${portalPath}/profile`}
                  className="acchub-sidebar-link"
                  onClick={(e) => {
                    e.preventDefault();
                    handleNav('/profile', false);
                  }}
                  aria-current={isActive('/profile') ? 'page' : undefined}
                >
                  <img src={profilIcon} alt="" className="acchub-sidebar-icon" aria-hidden />
                  <span className="acchub-sidebar-label">{__('Profil')}</span>
                  <StaticExpandIcon />
                </a>
              </div>
            </li>
            <li className="acchub-sidebar-item">
              <div className="acchub-sidebar-itemHeader">
                <a
                  href={bootstrap.logoutUrl}
                  className="acchub-sidebar-link"
                  onClick={(e) => {
                    e.preventDefault();
                    handleNav('', true);
                  }}
                >
                  <img src={deconnexionIcon} alt="" className="acchub-sidebar-icon" aria-hidden />
                  <span className="acchub-sidebar-label">{__('Déconnexion')}</span>
                  <StaticExpandIcon />
                </a>
              </div>
            </li>
          </ul>
        </div>
      </nav>

      <div className="acchub-sidebar-profile">
        <ProfileAvatar src={user.avatarUrl} alt={user.displayName} className="acchub-sidebar-profileAvatar" />
        <div className="acchub-sidebar-profileText">
          <div className="acchub-sidebar-profileName">{user.displayName}</div>
          <div className="acchub-sidebar-profileEmail">{user.email}</div>
        </div>
      </div>

      {showLogoutModal && (
        <div className="thn-modal-overlay" role="presentation" onClick={() => setShowLogoutModal(false)}>
          <div
            className="thn-modal thn-modal--small"
            role="dialog"
            aria-modal="true"
            aria-labelledby="logout-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="thn-modal__close"
              onClick={() => setShowLogoutModal(false)}
              aria-label={__('Fermer')}
            >
              <span aria-hidden>×</span>
            </button>
            <div className="thn-modal__header">
              <h3 id="logout-modal-title">{__('Déconnexion')}</h3>
            </div>
            <div className="thn-modal__body">
              <p>{__('Voulez-vous vraiment vous déconnecter ?')}</p>
            </div>
            <div className="thn-modal__footer">
              <button type="button" className="btn-secondary" onClick={() => setShowLogoutModal(false)}>
                {__('Annuler')}
              </button>
              <button type="button" className="btn-primary" onClick={confirmLogout}>
                {__('Se déconnecter')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortalSidebar;
